import { defineComponent as _defineComponent } from 'vue'
import axios from 'axios'
import {
  getCurrentInstance,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch
} from 'vue'
import { AD_NETWORKS } from '@/config/ad-networks'
import { useAuth } from '@/composables/useAuth'
import { AUTH_STATE_CHANGED, emitter } from '@/plugins/emitter'
import {
  getConsentFlag,
  setConsentFlag,
  getPromptableNetworks,
  getPromptCount,
  incrementPromptCount,
  AD_PROMPT_MAX,
  fetchAllFlagsFromServer,
  saveConsentFlagToServer,
  fireNetwork
} from '@/utils/ad-consent'

type AdNetwork = (typeof AD_NETWORKS)[number]
type ConsentFlags = Record<string, string>
type VarunaClient = {
  endPoints: {
    getAdConsentFlags: string
    updateAdConsentFlag: string
  }
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AdConsentModal',
  setup(__props) {

/**
 * 広告同意モーダル（vue-final-modal）。
 * ログイン時は mitt の auth_state_changed、非ログインは useAuth().whenAuthReady()。
 * 手動表示は mitt の show-ad-consent-modal。vue-js-modal / event bus は使わない。
 */
const SHOW_AD_CONSENT_MODAL = 'show-ad-consent-modal'

const vm = getCurrentInstance()!.proxy as unknown as { $varuna: VarunaClient }
const varuna = vm.$varuna
const { user, isAuthenticated, getClient, whenAuthReady } = useAuth()

const visible = ref(false)
const undecidedNetworks = ref<AdNetwork[]>([])
const modalShown = ref(false)
const decisionMade = ref(false)
const scrolledToBottom = ref(false)
const scrollBody = ref<HTMLElement | null>(null)
let authCheckInFlight = false

function currentUid(): string | undefined {
  return getClient()?.user?.uid ?? user.value?.uid
}

function currentIsAuthenticated(): boolean {
  return !!(getClient()?.user?.uid || isAuthenticated.value)
}

function onVisibleInput(value: boolean) {
  visible.value = value
}

function closeModal() {
  visible.value = false
}

function showAll() {
  undecidedNetworks.value = AD_NETWORKS.slice()
  visible.value = true
}

function onModalOpened() {
  incrementPromptCount()
  decisionMade.value = false
  scrolledToBottom.value = false
  nextTick(() => {
    const el = scrollBody.value
    if (!el) return
    if (el.scrollHeight <= el.clientHeight + 2) scrolledToBottom.value = true
  })
}

function onBodyScroll() {
  const el = scrollBody.value
  if (!el) return
  scrolledToBottom.value = el.scrollTop + el.clientHeight >= el.scrollHeight - 8
}

async function persistFlags(agreed: boolean) {
  const uid = currentUid()
  const authenticated = currentIsAuthenticated()
  for (const network of undecidedNetworks.value) {
    if (authenticated && uid) {
      await saveConsentFlagToServer(axios, varuna, uid, network.id, agreed)
    } else {
      setConsentFlag(network.id, agreed ? '1' : '0')
    }
    if (agreed) fireNetwork(network.id)
  }
}

async function onAgreeAll() {
  decisionMade.value = true
  await persistFlags(true)
  visible.value = false
}

async function onModalClosed() {
  if (decisionMade.value) return
  decisionMade.value = true
  await persistFlags(false)
}

async function onAuthStateChanged() {
  if (modalShown.value || authCheckInFlight) return
  const uid = currentUid()
  if (!uid) return
  authCheckInFlight = true
  try {
    const flags = (await fetchAllFlagsFromServer(
      axios,
      varuna,
      uid
    )) as ConsentFlags
    for (const network of AD_NETWORKS) {
      const dbVal = flags[network.id]
      if (dbVal === '1' || dbVal === '0') continue
      const cookieVal = getConsentFlag(network.id)
      if (cookieVal === '1' || cookieVal === '0') {
        await saveConsentFlagToServer(
          axios,
          varuna,
          uid,
          network.id,
          cookieVal === '1'
        )
        flags[network.id] = cookieVal
      }
    }
    if (modalShown.value) return
    if (getPromptCount() >= AD_PROMPT_MAX) return
    undecidedNetworks.value = AD_NETWORKS.filter((n) => flags[n.id] !== '1')
    if (undecidedNetworks.value.length > 0) {
      modalShown.value = true
      visible.value = true
    }
  } finally {
    authCheckInFlight = false
  }
}

function maybeShowForGuest() {
  if (modalShown.value || currentIsAuthenticated()) return
  if (getPromptCount() >= AD_PROMPT_MAX) return
  undecidedNetworks.value = getPromptableNetworks()
  if (undecidedNetworks.value.length > 0) {
    modalShown.value = true
    visible.value = true
  }
}

watch(visible, (open) => {
  if (open) {
    onModalOpened()
    return
  }
  onModalClosed()
})

onBeforeMount(() => {
  emitter.on(AUTH_STATE_CHANGED, onAuthStateChanged)
  emitter.on(SHOW_AD_CONSENT_MODAL, showAll)
  whenAuthReady().then((authUser) => {
    if (authUser) {
      onAuthStateChanged()
      return
    }
    maybeShowForGuest()
  })
})

onBeforeUnmount(() => {
  emitter.off(AUTH_STATE_CHANGED, onAuthStateChanged)
  emitter.off(SHOW_AD_CONSENT_MODAL, showAll)
})

return { __sfc: true,SHOW_AD_CONSENT_MODAL, vm, varuna, user, isAuthenticated, getClient, whenAuthReady, visible, undecidedNetworks, modalShown, decisionMade, scrolledToBottom, scrollBody, authCheckInFlight, currentUid, currentIsAuthenticated, onVisibleInput, closeModal, showAll, onModalOpened, onBodyScroll, persistFlags, onAgreeAll, onModalClosed, onAuthStateChanged, maybeShowForGuest }
}

})